import type { ReactNode } from 'react'
import { useEffect, useRef, useState } from 'react'
import dayjs from 'dayjs'
import { toast } from 'sonner'
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer'
import { Card } from '@/components/ui/card'
import { Dialog, DialogContent } from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { DetailSkeleton } from '@/components/ui/skeleton-presets'
import { Progress } from '@/components/ui/progress'
import { TaskTripInfo } from '@/components/TaskTripInfo'
import { TaskStatusBadge } from '@/components/TaskStatusBadge'
import { TaskPhotos } from '@/components/TaskPhotos'
import {
  YesNoField,
  TextField,
  TextAreaField,
  NumberField,
} from '@/components/ChecklistFields'
import { CircleCheck, MoreVertical, X, Loader2 } from '@/components/icons'
import { useTask, useTaskMarkRead } from '@/lib/hooks/task'
import { useS3GetDownloadUrl } from '@/lib/hooks/s3'
import { exportTaskAsPdf } from '@/lib/task-pdf'
import { getInitials } from '@/lib/utils'

type ReviewTask = NonNullable<ReturnType<typeof useTask>['data']>

type ItemType = 'YesNo' | 'Text' | 'TextArea' | 'Number'

interface ChecklistItem {
  id: string
  label: string
  type: ItemType
  isRequired: boolean
  booleanResponse: boolean | null
  textResponse: string | null
  numberResponse: number | null
}

interface OperationsTaskReviewDrawerProps {
  taskId: string | null
  onOpenChange: (open: boolean) => void
}

function hasResponse(item: ChecklistItem): boolean {
  return (
    item.booleanResponse !== null ||
    (item.textResponse !== null && item.textResponse !== '') ||
    item.numberResponse !== null
  )
}

export function OperationsTaskReviewDrawer({ taskId, onOpenChange }: OperationsTaskReviewDrawerProps) {
  const { data: task, isLoading } = useTask(taskId)
  const markRead = useTaskMarkRead()
  const getDownloadUrl = useS3GetDownloadUrl()
  const [isExporting, setIsExporting] = useState(false)
  const markedRef = useRef<string | null>(null)

  // Opening a submitted task automatically marks it as read.
  useEffect(() => {
    if (!task || task.status !== 'Submitted') return
    if (markedRef.current === task.id) return
    markedRef.current = task.id
    markRead.mutate({ id: task.id })
  }, [task, markRead])

  const handleExport = async () => {
    if (!task) return
    setIsExporting(true)
    try {
      await exportTaskAsPdf({
        task: {
          name: task.name,
          status: task.status,
          items: task.items,
          photoS3Keys: task.photoS3Keys,
          submittedBy: task.submittedBy,
          submittedAt: task.submittedAt,
          tripLeg: task.tripLeg,
        },
        getDownloadUrl: async (s3Key) => {
          const { downloadUrl } = await getDownloadUrl.mutateAsync({ s3Key })
          return downloadUrl
        },
      })
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to export PDF')
    } finally {
      setIsExporting(false)
    }
  }

  const responded = task?.items.filter(hasResponse).length ?? 0
  const total = task?.items.length ?? 0
  const progress = total === 0 ? 0 : Math.round((responded / total) * 100)
  const allComplete = total > 0 && responded === total

  return (
    <Drawer open={!!taskId} onOpenChange={onOpenChange}>
      <DrawerContent className="bg-surface-L0 max-h-[85dvh] flex flex-col">
        <DrawerHeader className="flex items-center justify-between p-4">
          <DrawerTitle className="text-lg font-semibold text-content-primary-bold truncate">
            {task?.name ?? 'Task'}
          </DrawerTitle>
          <div className="flex items-center gap-2 shrink-0">
            <DropdownMenu>
              <DropdownMenuTrigger
                className="size-8 rounded-full bg-surface-L2 flex items-center justify-center hover:bg-surface-L2/80 transition-colors outline-none"
                aria-label="More options"
              >
                <MoreVertical className="size-4 text-content-primary-subtle" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  className="text-content-primary-bold focus:text-content-primary-bold"
                  onClick={handleExport}
                  disabled={!task || isExporting}
                >
                  {isExporting ? 'Exporting...' : 'Export as PDF'}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <button
              onClick={() => onOpenChange(false)}
              className="size-8 rounded-full bg-surface-L2 flex items-center justify-center hover:bg-surface-L2/80 transition-colors"
              aria-label="Close"
            >
              <X className="size-4 text-content-primary-subtle" />
            </button>
          </div>
        </DrawerHeader>

        <div
          className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 pb-4"
          style={{ WebkitOverflowScrolling: 'touch' }}
        >
          {isLoading || !task ? (
            <DetailSkeleton />
          ) : (
            <div className="flex flex-col gap-4">
              <TaskMetaCard task={task} />

              {task.tripLeg && <TaskTripInfo tripLeg={task.tripLeg} />}

              <div className="flex items-center gap-2 py-1">
                <Progress
                  value={progress}
                  className="h-1 flex-1 bg-background-brand-bold/20"
                  indicatorClassName="bg-background-brand-bold"
                />
                <span className="text-xs font-semibold text-content-primary-subtle">
                  {responded} of {total}
                </span>
                {allComplete && <CircleCheck className="size-5 text-content-positive-default" />}
              </div>

              {task.items.length === 0 ? (
                <p className="text-sm text-content-primary-subtle py-8 text-center">
                  This task has no items.
                </p>
              ) : (
                task.items.map((item) => <ReadOnlyField key={item.id} item={item} />)
              )}

              {task.photoS3Keys.length > 0 && (
                <TaskPhotos taskId={task.id} photoS3Keys={task.photoS3Keys} readOnly />
              )}
            </div>
          )}
        </div>
      </DrawerContent>

      <Dialog open={isExporting}>
        <DialogContent
          className="bg-surface-L0 rounded-lg border border-border-L2 shadow-lg p-8 max-w-[320px] gap-2 [&>button]:hidden"
          onInteractOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          <div className="flex flex-col items-center gap-4">
            <Loader2 className="w-8 h-8 animate-spin text-content-primary-subtle" />
            <p className="text-lg font-bold text-content-primary-bold text-center tracking-[-1px] w-full leading-7">
              Exporting PDF
            </p>
            <p className="text-sm text-content-primary-subtle text-center w-full leading-5">
              Preparing your checklist and photos. This may take a moment.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </Drawer>
  )
}

function TaskMetaCard({ task }: { task: ReviewTask }) {
  const submittedBy = task.submittedBy
  const submittedAt = task.submittedAt ? dayjs(task.submittedAt) : null
  const aircraft = task.aircraft ?? task.tripLeg?.aircraft ?? null
  const crew = task.assignedCrew

  return (
    <Card className="flex flex-col gap-0 px-4 py-0 border-border-L1">
      <MetaRow label="Status">
        <TaskStatusBadge status={task.status} />
      </MetaRow>

      {submittedBy && (
        <MetaRow label="Submitted by">
          <div className="flex flex-col items-end">
            <span className="text-sm font-medium text-content-primary-bold">
              {submittedBy.firstName} {submittedBy.lastName}
            </span>
            {submittedAt && (
              <span className="text-xs text-content-primary-subtle">
                {submittedAt.format('DD MMM YYYY, HH:mm')}
              </span>
            )}
          </div>
        </MetaRow>
      )}

      {aircraft && (
        <MetaRow label="Aircraft">
          <span className="text-sm font-medium text-content-primary-bold">
            {aircraft.registration}
          </span>
        </MetaRow>
      )}

      {crew && (
        <MetaRow label="Assigned crew">
          <div className="flex items-center gap-2 min-w-0">
            <div
              className="size-6 shrink-0 rounded-full flex items-center justify-center text-xs font-medium text-content-primary-inverse"
              style={{ backgroundColor: crew.avatarColor }}
            >
              {getInitials(crew.firstName, crew.lastName)}
            </div>
            <span className="text-sm font-medium text-content-primary-bold truncate">
              {crew.name ?? `${crew.firstName} ${crew.lastName}`}
            </span>
          </div>
        </MetaRow>
      )}
    </Card>
  )
}

function MetaRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3 py-3 border-b border-border-L1 last:border-b-0">
      <span className="text-sm text-content-primary-subtle shrink-0">{label}</span>
      <div className="min-w-0 text-right">{children}</div>
    </div>
  )
}

function ReadOnlyField({ item }: { item: ChecklistItem }) {
  const common = { label: item.label, isRequired: item.isRequired, readOnly: true }

  if (item.type === 'YesNo') {
    return <YesNoField {...common} value={item.booleanResponse} />
  }
  if (item.type === 'TextArea') {
    return <TextAreaField {...common} value={item.textResponse} />
  }
  if (item.type === 'Number') {
    return <NumberField {...common} value={item.numberResponse} />
  }
  return <TextField {...common} value={item.textResponse} />
}
